import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAnimes } from '../redux/slices/animeSlice';
import { fetchSeries } from '../redux/slices/seriesSlice';
import { fetchSeasonsByAnimeId, fetchSeasonsBySeriesId } from '../redux/slices/seasonSlice';
import { fetchEpisodesBySeasonId, deleteEpisode, updateEpisode } from '../redux/slices/episodeSlice';
import ConfirmModal from './ConfirmModal';
import EditEpisodeModal from './EditEpisodeModal';
import BulkEpisodeUpload from './BulkEpisodeUpload';
import AddEpisodeModal from './AddEpisodeModal';

const EpisodeTable = ({ seriesMode = false }) => {
  const dispatch = useDispatch();
  const { items: animes } = useSelector((state) => state.animes);
  const { items: series } = useSelector((state) => state.series);
  const { items: seasons } = useSelector((state) => state.seasons);
  const { items: episodes, status, error } = useSelector((state) => state.episodes);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedContentId, setSelectedContentId] = useState('');
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  
  // Estado para el modal de confirmación y mensajes de error
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [episodeToDelete, setEpisodeToDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);

  // Configuración según el tipo de contenido
  const contentType = seriesMode ? 'series' : 'anime';
  const contentItems = seriesMode ? series : animes;
  const contentLabel = seriesMode ? 'Serie' : 'Anime';

  useEffect(() => {
    if (seriesMode) {
      dispatch(fetchSeries());
    } else {
      dispatch(fetchAnimes());
    }
  }, [dispatch, seriesMode]);

  useEffect(() => {
    const storedSeasonId = localStorage.getItem("selectedSeasonId");
    if (storedSeasonId) {
      // Buscar la temporada y su contenido correspondiente
      const findSeasonAndContent = async () => {
        try {
          // Primero obtenemos todas las temporadas para encontrar a cuál contenido pertenece
          const fetchAction = seriesMode ? fetchSeasonsBySeriesId : fetchSeasonsByAnimeId;
          const response = await dispatch(fetchAction()).unwrap();
          if (response && response.length > 0) {
            const foundSeason = response.find(s => s.id === storedSeasonId);
            if (foundSeason && foundSeason.contentId) {
              // Seleccionar el contenido primero
              setSelectedContentId(foundSeason.contentId);
              
              // Luego seleccionamos la temporada
              setTimeout(() => {
                setSelectedSeasonId(storedSeasonId);
              }, 300); // Pequeño retraso para asegurar que las temporadas se carguen
              
              // Limpiar localStorage
              localStorage.removeItem("selectedSeasonId");
            }
          }
        } catch (err) {
          console.error("Error buscando temporada:", err);
        }
      };
      
      findSeasonAndContent();
    }
  }, [dispatch, seriesMode]);

  useEffect(() => {
    if (selectedContentId) {
      if (seriesMode) {
        dispatch(fetchSeasonsBySeriesId(selectedContentId));
      } else {
        dispatch(fetchSeasonsByAnimeId(selectedContentId));
      }
      setSelectedSeasonId(''); // Resetear la temporada seleccionada cuando cambia el contenido
    }
  }, [selectedContentId, dispatch, seriesMode]);

  useEffect(() => {
    if (selectedSeasonId) {
      dispatch(fetchEpisodesBySeasonId(selectedSeasonId));
    }
  }, [selectedSeasonId, dispatch]);

  // Filtrar episodios por término de búsqueda
  const filteredEpisodes = episodes.filter(episode =>
    episode.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calcular paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEpisodes = filteredEpisodes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEpisodes.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleContentChange = (e) => {
    setSelectedContentId(e.target.value);
    setCurrentPage(1);
  };

  const handleSeasonChange = (e) => {
    setSelectedSeasonId(e.target.value);
    setCurrentPage(1);
  };

  const handleEdit = (episode) => {
    setEditingEpisode(episode);
    setIsEditModalOpen(true);
  };

  const handleSaveEpisode = async (data) => {
    try {
      await dispatch(updateEpisode(data)).unwrap();
      setIsEditModalOpen(false);
      setEditingEpisode(null);
      
      // Mostrar mensaje de éxito
      setErrorMessage('Episodio actualizado exitosamente');
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
        setErrorMessage('');
      }, 3000);
      
      // Recargar los episodios
      if (selectedSeasonId) {
        dispatch(fetchEpisodesBySeasonId(selectedSeasonId));
      }
    } catch (err) {
      console.error('Error al actualizar:', err);
      setErrorMessage('Error al actualizar el episodio: ' + (err.message || ''));
      setShowError(true);
    }
  };
  
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingEpisode(null);
  };

  const handleDelete = (episode) => {
    setEpisodeToDelete(episode);
    setIsConfirmModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!episodeToDelete) return;
    
    try {
      const resultAction = await dispatch(deleteEpisode(episodeToDelete.id));
      if (deleteEpisode.rejected.match(resultAction)) {
        // Mostrar mensaje de error
        setErrorMessage(resultAction.payload.message || 'Error al eliminar el episodio');
        setShowError(true);
        // Ocultar después de 8 segundos
        setTimeout(() => {
          setShowError(false);
          setErrorMessage('');
        }, 8000);
      }
    } catch (err) {
      console.error('Error al eliminar:', err);
      setErrorMessage('Error al eliminar el episodio');
      setShowError(true);
      // Ocultar después de 8 segundos
      setTimeout(() => {
        setShowError(false);
        setErrorMessage('');
      }, 8000);
    } finally {
      // Cerrar el modal y limpiar el estado
      setIsConfirmModalOpen(false);
      setEpisodeToDelete(null);
    }
  };
  
  const cancelDelete = () => {
    setIsConfirmModalOpen(false);
    setEpisodeToDelete(null);
  };

  const handleBulkUpload = () => {
    setShowBulkUploadModal(true);
  };

  const handleCloseBulkUpload = () => {
    setShowBulkUploadModal(false);
  };

  const handleAddEpisode = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleAddSuccess = () => {
    // Recargar los episodios después de agregar uno nuevo
    if (selectedSeasonId) {
      dispatch(fetchEpisodesBySeasonId(selectedSeasonId));
    }
    // Mostrar mensaje de éxito
    setErrorMessage('Episodio agregado exitosamente');
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
      setErrorMessage('');
    }, 3000);
  };

  const handleBulkUploadSuccess = () => {
    // Recargar los episodios después de una carga exitosa
    if (selectedSeasonId) {
      dispatch(fetchEpisodesBySeasonId(selectedSeasonId));
    }
    // Mostrar mensaje de éxito
    setErrorMessage('Episodios cargados exitosamente');
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
      setErrorMessage('');
    }, 3000);
  };

  if (status === 'loading') {
    return <div className="text-center p-4">Cargando episodios...</div>;
  }

  if (status === 'failed') {
    return <div className="text-center p-4 text-red-500">Error: {error}</div>;
  }

  return (
    <div className="space-y-4">
      {showError && (
        <div className="bg-red-600 text-white p-4 rounded-md mb-4 text-center font-bold">
          {errorMessage}
        </div>
      )}
      
      {/* Modal de confirmación */}
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        title="Confirmar eliminación de episodio"
        message={
          episodeToDelete ? (
            `¿Estás seguro de que deseas eliminar el siguiente episodio?

Episodio: ${episodeToDelete.episodeNumber} - "${episodeToDelete.name}"
${seasons.find(season => season.id === parseInt(episodeToDelete.seasonId))?.title || 'Temporada'}`
          ) : "Selecciona un episodio para eliminar"
        }
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      {/* Modal de edición */}
      <EditEpisodeModal 
        isOpen={isEditModalOpen}
        episode={editingEpisode}
        seasons={seasons}
        onClose={closeEditModal}
        onSave={handleSaveEpisode}
      />

      {/* Modal de agregar episodio */}
      {isAddModalOpen && (
        <AddEpisodeModal
          animeId={contentType === 'anime' ? selectedContentId : undefined}
          seriesId={contentType === 'series' ? selectedContentId : undefined}
          seasons={seasons.filter(season => season.contentId === parseInt(selectedContentId) && season.contentType === contentType)}
          onClose={handleCloseAddModal}
          onSuccess={handleAddSuccess}
        />
      )}

      {/* Modal de carga masiva */}
      {showBulkUploadModal && (
        <BulkEpisodeUpload
          seasonId={selectedSeasonId}
          onClose={handleCloseBulkUpload}
          onSuccess={handleBulkUploadSuccess}
        />
      )}

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="content-select" className="block text-sm font-medium text-gray-300">
            Seleccionar {contentLabel}
          </label>
          <select
            id="content-select"
            value={selectedContentId}
            onChange={handleContentChange}
            className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-800 text-gray-300"
          >
            <option value="">Selecciona {seriesMode ? 'una serie' : 'un anime'}</option>
            {contentItems.map((content) => (
              <option key={content.id} value={content.id}>
                {content.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="season-select" className="block text-sm font-medium text-gray-300">
            Seleccionar Temporada
          </label>
          <select
            id="season-select"
            value={selectedSeasonId}
            onChange={handleSeasonChange}
            disabled={!selectedContentId}
            className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-800 text-gray-300"
          >
            <option value="">Selecciona una temporada</option>
            {seasons
              .filter(season => season.contentId === parseInt(selectedContentId) && season.contentType === contentType)
              .map((season) => (
                <option key={season.id} value={season.id}>
                  {season.title} (Temporada {season.seasonNumber})
                </option>
              ))}
          </select>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <input
          type="text"
          placeholder="Buscar episodios..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border border-gray-600 rounded-md px-4 py-2 bg-gray-800 text-gray-300"
        />
        
        {selectedSeasonId && (
          <div className="space-x-2">
            <button
              onClick={handleAddEpisode}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
            >
              Añadir Episodio
            </button>
            <button
              onClick={handleBulkUpload}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
            >
              Carga Masiva
            </button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-gray-900 divide-y divide-gray-700">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Número</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Nombre</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Duración</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Tamaño</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Formato</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody className="bg-gray-800 divide-y divide-gray-700">
            {selectedSeasonId ? (
              currentEpisodes.length > 0 ? (
                currentEpisodes.map(episode => (
                  <tr key={episode.id} className="hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-gray-300">{episode.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-300">{episode.episodeNumber}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-4 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-blue-800 text-blue-100">
                        {episode.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-300">{episode.runtime} min</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-300">
                      {(episode.fileSize / (1024 * 1024)).toFixed(2)} MB
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-300">{episode.mimeType}</td>
                    <td className="px-6 py-4 whitespace-nowrap space-x-2">
                      <button
                        onClick={() => handleEdit(episode)}
                        className="text-blue-400 hover:text-blue-200"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(episode)}
                        className="text-red-400 hover:text-red-200"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-white">
                    No hay episodios disponibles para esta temporada
                  </td>
                </tr>
              )
            ) : (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-white">
                  Seleccione una temporada para ver sus episodios
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-4">
          <nav>
            <ul className="flex">
              {(() => {
                const pages = [];
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => handlePageChange(i)}
                      className={`mx-1 px-3 py-1 rounded ${
                        currentPage === i
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {i}
                    </button>
                  );
                }
                return pages;
              })()}
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default EpisodeTable;
